/**
 * AnalysisReviewCard — 문제 분석 v2 검수 카드 (재사용 독립 컴포넌트).
 *
 * problemId 하나만 받아 스스로 현황을 읽고(요청→폴링→검수→승인) 완결한다 — problem-hub 우측
 * 패널과 "AI 시험지 생성" 문항별 검수 양쪽에 그대로 삽입되는 것이 요구사항
 * (FE-reply-analysis-review-screen.md §1 · BE-20260824-problem-hub-v2.md 재사용 규약).
 *
 * 데이터 규칙(계약):
 *  - 화면은 values(원본+수정 병합 정본)를 그린다. 원본 result 는 불변 — 수정은 result_edited 로.
 *  - current(문제은행 현재값)가 있는 슬롯은 승인 시 덮어쓰기 → 경고 배지.
 *  - 승인 전까지 문제은행은 절대 변하지 않는다. 재분석은 revision 이 쌓인다(이력).
 *  - PATCH·approve 에 expected_updated_at 동봉(동시 수정 409).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Sparkles, Check, Trash2, RotateCw } from 'lucide-react';
import { Button, ConceptChip, AiRunModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { difficultyOptionLabel } from './problemApi';
import { fetchConceptDictV2, matchConcept } from '../ExamPapersV2/conceptBadge';
import { getAnalysisStatusDisplay as ocrAnalysisStatusDisplay } from '../OcrHub/ocrApi';

import { Blocks } from '../OcrHub/BlockRenderer';
import {
  fetchProblemAnalysis, fetchAnalysis, requestAnalysis, patchAnalysis, approveAnalysis, discardAnalysis,
  ANALYSIS_POLL_MS, AUTH_REDIRECT } from './analysisApi';
import AnalysisTargetChecks from './AnalysisTargetChecks';
import AnswerView from './AnswerView';
import {
  ANALYSIS_TARGET_KEYS, ANALYSIS_TARGET_LABEL, presenceFromCurrent, defaultAnalysisTargets, toAnalysisOptions,
  hasAnyTarget, emptySlotReason, willApply, solutionAnswerCheck,
} from './analysisTargets';

// 이 API(§2-3)의 100은 '미실행'이 아니라 '대기(요청 접수·큐)'다 — OCR-hub 맵을 그 의미로 보정
const getAnalysisStatusDisplay = (status) => (
  status === 100
    ? { text: '분석 대기', cls: 'bg-gray-100 text-gray-800' }
    : ocrAnalysisStatusDisplay(status)
);

const POLLING_STATUSES = [100, 110, 150];

/**
 * 저장(PATCH)·승인(approve) 실패를 사람 말로 옮긴다.
 *
 * 갈림은 **HTTP 코드가 아니라 봉투의 `data.reason`** 이다(계약 §3). message 는 사람에게 보여
 * 주려고 백엔드가 쓴 문구라 언제든 다듬어지고, 그 문자열로 분기하면 어느 날 조용히 어긋난다.
 * 코드로 갈리는 것만 코드로 가른다.
 */
const REVIEW_ERROR_TEXT = {
  // 낙관락 — 내가 이 검수면을 읽은 뒤 누군가(또는 다른 탭의 나)가 먼저 고쳤다.
  stale: '이 분석이 그 사이 다른 곳에서 수정됐습니다 — 최신을 받은 뒤 다시 시도하세요.',
  // 승인했는데 문제은행에 넣을 것이 하나도 없다. 예전에는 이것도 200 "approved" 였고,
  // 화면은 "반영했습니다"라고 말하는데 은행 값은 그대로인 거짓 보고가 났다(계약 §3 · 감사 C2-②).
  nothing_to_apply: '반영할 것이 없습니다 — 이미 문제은행에 들어가 있거나, 승인할 값이 비어 있습니다.',
};

/** 낙관락에 막힌 실패인가 — 화면이 [최신 받기]를 내밀어야 하는 갈래 */
function isStaleError(e) {
  return e?.data?.reason === 'stale' || (e?.httpStatus === 409 && e?.data?.reason == null);
}

function reviewErrorText(e, fallback) {
  const reason = e?.data?.reason;
  if (reason && REVIEW_ERROR_TEXT[reason]) return REVIEW_ERROR_TEXT[reason];
  // reason 없는 409 는 낙관락 하나뿐이다 — 계약 §3 이전 배포분과 섞여 돌 때의 안전망.
  if (e?.httpStatus === 409) return REVIEW_ERROR_TEXT.stale;
  // 400 은 "값의 모양"이 틀린 것이다(계약 §3 · 감사 C2): 풀이 editorState 가 객체가 아니거나
  // root.children 이 비었거나, solution_blocks 원소에 kind 가 없거나, 난이도·개념에 boolean.
  // 어느 칸인지는 봉투 message 에만 있으므로 그대로 붙인다 — 여기서 뭉개면 고칠 곳을 못 찾는다.
  if (e?.httpStatus === 400) {
    return `보낼 수 없는 값이 있습니다 — ${e.message || '풀이·난이도·개념 값의 형식을 확인하세요.'}`;
  }
  return e?.message || fallback;
}

// 결과가 빈 칸의 이유 — analysisTargets.emptySlotReason 의 값. 없는 이유는 「반영 안 됨」으로 묶는다.
const EMPTY_SLOT_BADGE = {
  not_requested: { text: '요청 안 함', title: '이번 분석에서 이 항목을 요청하지 않았습니다 — 승인해도 문제은행 값은 그대로입니다' },
  kept_existing: { text: '이미 있어 안 만듦', title: '문제은행에 이미 있어 만들지 않았습니다 — 새로 받으려면 [AI 분석]에서 이 항목을 켜세요' },
};
const EMPTY_SLOT_FALLBACK = { text: '반영 안 됨', title: '이 칸은 결과가 비어 있어 승인해도 문제은행 값이 그대로입니다' };

const SOLUTION_EMPTY_TEXT = {
  not_requested: '이번 분석에서 풀이를 요청하지 않았습니다 — 문제은행 풀이는 그대로입니다.',
  kept_existing: '문제은행에 풀이가 있어 만들지 않았습니다 — 새로 받으려면 [AI 분석]에서 풀이를 켜세요.',
};

/**
 * 정답 칸 — 문제 보기 탭의 정답과 **동일하게**(오너 지시 2026-09-27).
 *  - 평소: 보기 상자. ProblemViewTab 보기 모드의 정답 상자와 같은 치수(rounded-xl · border-gray-100 · px-3 py-2)에
 *    같은 부품(AnswerView — 18px, LaTeX 는 KaTeX)으로 그린다. 두 번 누르면 편집으로 바뀐다(같은 title).
 *  - 편집: LaTeX 원문 입력칸. ProblemViewTab AnswerField 의 입력칸과 같은 치수(rounded-xl · px-3 py-2 · text-sm).
 *    Enter·칸 밖 = 확정, Esc = 들어올 때 값으로 되돌림. 저장은 종전대로 [저장]·[승인] — 값은 부모가 쥔다.
 * 이 카드에는 [보기|편집] 전환이 없어 키보드로도 열려야 한다 — 보기 상자에 Enter·Space 와 포커스 테두리를 단다
 * (AnswerField 보기 상자와 같은 규칙). 두 번째 눌림의 낱말 고르기는 막는다(보기 모드 상자와 같은 규칙).
 */
function AnswerBox({ value, onChange, disabled, placeholder }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const enteredWith = useRef(''); // Esc 로 되돌릴 값 — 편집에 들어간 순간의 원문

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const open = () => {
    if (disabled) return;
    enteredWith.current = value;
    setEditing(true);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            setEditing(false);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onChange(enteredWith.current);
            setEditing(false);
          }
        }}
        placeholder={placeholder}
        className="w-full min-w-0 rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
      />
    );
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      /* aria-label 은 내용(AnswerView)을 덮는다 — 값을 이름에 담지 않으면 정답이 아예 안 읽힌다 */
      aria-label={`정답 ${String(value ?? '').trim() || '없음'} — 두 번 누르거나 Enter 로 수정`}
      title={disabled ? undefined : '두 번 누르면 편집으로 바뀝니다'}
      onMouseDown={(e) => { if (e.detail >= 2) e.preventDefault(); }}
      onDoubleClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          open();
        }
      }}
      className="min-w-0 flex-1 rounded-xl border border-gray-100 px-3 py-2 focus:border-primary focus:outline-none"
    >
      <AnswerView value={value} />
    </div>
  );
}

function SlotBadge({ currentValue, filled = true, reason = null }) {
  // 결과가 빈 칸은 승인이 아무것도 바꾸지 않는다 — 「덮어쓰기/새로 채움」은 거짓이 되므로 이유를 대신 띄운다
  // (오너 결정 2026-09-27 D3). 체크를 끄고 다시 분석하면 이 칸이 흔해진다.
  if (!filled) {
    const b = EMPTY_SLOT_BADGE[reason] || EMPTY_SLOT_FALLBACK;
    return (
      <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500" title={b.title}>
        {b.text}
      </span>
    );
  }
  // current 값이 있으면 승인이 그 값을 덮어쓴다 — 계약이 권고하는 경고 배지
  return currentValue != null ? (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" title="문제은행에 이미 값이 있어 승인 시 덮어씁니다">
      덮어쓰기
    </span>
  ) : (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700" title="문제은행이 비어 있어 새로 채워집니다">
      새로 채움
    </span>
  );
}

/**
 * @param canApprove [승인]·[폐기] 를 세울지 — 서버가 교사에게 403 으로 막은 두 경로다
 *   (`POST /problem-analysis/analysis/{id}/approve`·`/discard`, BE-20260910 회신 §1-2).
 * @param canRunAi [AI 분석 요청]/[AI 분석] 을 세울지 — 서버는 열려 있고 **LLM 비용** 때문에 화면에서만 감춘다.
 *
 * 둘 다 기본값이 허용(true)인 이유: 이 카드를 다른 화면에 꽂는 날 prop 을 빠뜨리면
 * 버튼이 **말없이 사라진다**. 사라진 것은 아무도 못 보지만 잘못 보이는 것은 곧 들킨다.
 * 판정 자체는 여기서 하지 않는다 — 담는 쪽(pages/ProblemHub.js)이 roles.js 를 읽어 내려 준다.
 *
 * [저장](PATCH /analysis/{id})은 교사에게 열려 있다 — 여기서 가르지 않는다.
 */
function AnalysisReviewCard({
  problemId, onChanged = null, compact = false, onDirtyChange = null,
  canApprove = true, canRunAi = true, reloadKey = 0,
}) {
  const { getAccessToken } = useAuth();
  const [snap, setSnap] = useState(null); // { latest, history, current, state }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null); // { type, text }
  const [busy, setBusy] = useState(null); // 'request'|'save'|'approve'|'discard'|null
  // AI 요청 창 — 분석 버튼이 바로 실행하지 않고 이것을 연다.
  // 재분석 확인(window.confirm)의 문구를 이 창의 안내·경고로 옮겼다.
  const [aiOpen, setAiOpen] = useState(false);
  // 창의 「이번에 만들 것」 체크 넷 — 열 때마다 은행 현재값으로 다시 세운다(openAiWindow).
  const [targets, setTargets] = useState(() => defaultAnalysisTargets(null));
  // 409 stale 을 만났다 — "최신을 받은 뒤 다시" 라고 말하려면 받을 손잡이도 함께 내밀어야 한다.
  // 문구만 두면 사용자가 할 수 있는 일이 없는 막다른 화면이 된다.
  const [staleHint, setStaleHint] = useState(false);

  // 편집 상태 — latest.values 로 seed
  const [conceptIds, setConceptIds] = useState([]);
  const [difficulty, setDifficulty] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [conceptQuery, setConceptQuery] = useState('');
  const [concepts, setConcepts] = useState([]);
  const seededRef = useRef({ conceptIds: [], difficulty: '', answer: '' });
  const problemRef = useRef(problemId); // in-flight 응답이 다른 문제 화면을 덮지 않도록
  const pollRef = useRef(null);

  useEffect(() => { problemRef.current = problemId; }, [problemId]);

  const latest = snap?.latest || null;
  const current = latest?.current ?? snap?.current ?? null;
  const conceptNames = latest?.concept_names || {};
  const values = latest?.values || null;

  const seedEdits = useCallback((detail) => {
    const v = detail?.values || {};
    const cids = Array.isArray(v.concept_ids) ? v.concept_ids : [];
    setConceptIds(cids);
    setDifficulty(v.difficulty != null ? String(v.difficulty) : '');
    setAnswerText(v.answer ?? '');
    seededRef.current = {
      conceptIds: cids,
      difficulty: v.difficulty != null ? String(v.difficulty) : '',
      answer: v.answer ?? '',
    };
    setConceptQuery('');
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (problemId == null) return;
    const target = problemId;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const d = await fetchProblemAnalysis(target, getAccessToken);
      if (String(target) !== String(problemRef.current)) return;
      setSnap(d);
      if (d?.latest?.status === 200) seedEdits(d.latest);
    } catch (e) {
      if (String(target) !== String(problemRef.current)) return;
      if (silent) {
        // 폴링·후속 갱신의 일시 실패로 화면을 무너뜨리지 않는다 — 기존 snap을 유지하면
        // polling 조건도 살아 있어 다음 tick에 재시도된다.
        setToast({ type: 'error', text: '갱신에 실패했습니다 — 자동으로 재시도합니다.' });
        return;
      }
      setError(e.message || '분석 현황을 불러오지 못했습니다.');
      setSnap(null);
    } finally {
      if (String(target) === String(problemRef.current) && !silent) setLoading(false);
    }
  }, [problemId, getAccessToken, seedEdits]);

  useEffect(() => {
    setSnap(null);
    setToast(null);
    setStaleHint(false);
    load();
  }, [load]);

  // 처리 중이면 폴링(실측 중앙값 36초 — 6초 간격). 완료/실패에서 스스로 멈춘다.
  const polling = latest != null && POLLING_STATUSES.includes(latest.status);
  useEffect(() => {
    if (!polling) return undefined;
    pollRef.current = setInterval(() => load({ silent: true }), ANALYSIS_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [polling, load]);

  // 개념 검색용 전체 개념 목록 — 검수(200)에 들어왔을 때 1회.
  // v2 사전(fetchConceptDictV2 → GET /api/v2/mathcepts, 5분 모듈 캐시). 2026-09-01 까지는
  // v1(/api/mathcepts/temp)이었다 — 문제 화면의 마지막 v1 자리였고 오너 지시로 걷었다.
  // 실패하면 null(던지지 않는다) → 빈 배열로 두고, 개념 검색이 「찾는 개념이 없습니다」로 선다.
  useEffect(() => {
    if (latest?.status !== 200 || concepts.length > 0) return;
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((list) => { if (alive) setConcepts(list || []); });
    return () => { alive = false; };
  }, [latest?.status, concepts.length, getAccessToken]);

  const dirty = useMemo(() => {
    const s = seededRef.current;
    if ([...conceptIds].sort().join(',') !== [...s.conceptIds].sort().join(',')) return true;
    if (difficulty !== s.difficulty) return true;
    if ((answerText ?? '') !== (s.answer ?? '')) return true;
    return false;
  }, [conceptIds, difficulty, answerText]);

  // 칸마다 「승인하면 값이 실리는가」와 비었을 때의 이유 — 배지와 승인 경고가 같은 자로 잰다.
  const bankPresence = presenceFromCurrent(current);
  const slotFilled = (key) => willApply(key, { conceptIds, difficulty, answer: answerText }, seededRef.current, latest);
  const slotReason = (key) => emptySlotReason(latest, key);

  // 창을 열 때마다 체크를 은행 현재값으로 다시 세운다 — 지난 요청의 선택이 다른 상태의 문제에 실리지 않게
  // (AiRunModal 이 지시 칸을 열 때마다 비우는 것과 같은 규칙).
  const openAiWindow = () => {
    setTargets(defaultAnalysisTargets(bankPresence));
    setAiOpen(true);
  };

  /**
   * 문제가 다른 곳에서 바뀌면(문제 편집의 개념·난이도 저장 · 다른 탭의 승인) 부모가 reloadKey 를 올린다.
   * 이 카드는 탭을 옮겨도 언마운트되지 않아(pages/ProblemHub 의 숨김 패널 — [수정 이력] 탭과 같은 사정)
   * 안 받으면 「은행 현재값」과 덮어쓰기·새로 채움 배지가 옛 값으로 남는다(2026-09-13 개념·난이도 직접 수정 도입).
   * 고치던 분석이 있으면 덮지 않는다 — [최신 받기](staleHint)를 내밀어 사용자가 고르게 한다.
   */
  const reloadSeenRef = useRef(reloadKey);
  useEffect(() => {
    if (reloadSeenRef.current === reloadKey) return;
    reloadSeenRef.current = reloadKey;
    if (dirty) {
      setToast({ type: 'info', text: '이 문제가 바뀌었습니다 — 고치던 분석이 있어 다시 읽지 않았습니다. [최신 받기]로 받을 수 있습니다.' });
      setStaleHint(true);
      return;
    }
    load({ silent: true });
  }, [reloadKey, dirty, load]);

  // [최신 받기] — 낙관락(409 stale)에 막혔을 때 푸는 유일한 길이다. 문구로 "최신을 받은 뒤
  // 다시 시도하세요"라고만 말하고 받을 손잡이를 주지 않으면 막다른 화면이 된다.
  // 서버 값으로 검수면을 다시 세우므로 저장하지 않은 수정은 사라진다 — 있으면 한 번 묻는다.
  const reloadFromServer = async () => {
    if (busy) return;
    if (dirty && !window.confirm(
      '서버의 최신 분석을 다시 받아옵니다.\n저장하지 않은 수정은 사라집니다.\n\n계속할까요?',
    )) return;
    setStaleHint(false);
    setToast(null);
    await load();
  };

  // 부모(허브)가 문제 이동·닫기 전에 물을 수 있도록 알린다 — 카드는 언마운트되면 편집이 사라진다
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  useEffect(() => { onDirtyRef.current?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyRef.current?.(false), []);

  const conceptMatches = useMemo(() => {
    const q = conceptQuery.trim().toLowerCase();
    if (!q) return [];
    return concepts
      .filter((c) => !conceptIds.includes(c.id))
      // 이름 필드는 ConceptChip 과 같은 순서 — v2 사전은 concept_name, 폴백 객체는 text
      .filter((c) => (c.concept_name || c.text || '').toLowerCase().includes(q) || String(c.id) === q || String(c.order ?? '') === q)
      .slice(0, 30);
  }, [conceptQuery, concepts, conceptIds]);

  const conceptLabel = (cid) =>
    matchConcept(concepts, cid) || { id: cid, text: conceptNames[String(cid)] || `#${cid}` };

  // ── 분석 요청/재분석 ──
  const handleRequest = async (userPrompt) => {
    if (busy) return;
    // 재분석 안내(새 revision·미저장 편집)는 창이 이미 보여 줬다. 여기서 다시 묻지 않는다.
    setBusy('request');
    setToast(null);
    try {
      // 만들 것은 넷을 늘 true/false 로 싣는다 — 생략하면 서버가 「은행에 있으면」 조용히 건너뛴다(analysisTargets).
      const d = await requestAnalysis(problemId, { options: toAnalysisOptions(targets), userPrompt }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setAiOpen(false);
      // dedup:'already' = 새 요청이 서지 않고 **진행 중이던 행이 그대로 끝난다**. 그러니 이번에 쓴
      // 지시도, 고른 항목도 어디에도 반영되지 않는다(BE-20260829-reply-fe-0828.md §4-1이 명시).
      // 지시는 실제로 쓴 사람에게만 말한다 — 안 쓴 사람에게는 없는 손실을 알리는 소음이다.
      // 고른 항목은 늘 실어 보내므로(진행 중인 분석과 다를 수 있다) 늘 말한다.
      const wrotePrompt = !!(userPrompt && userPrompt.trim());
      setToast({
        type: 'info',
        text: d.dedup === 'already'
          ? `이미 분석이 진행 중입니다 — 이어서 기다립니다. 이번에 고른 항목${wrotePrompt ? '과 쓴 지시는' : '은'} 반영되지 않습니다(진행 중인 분석이 그대로 끝납니다).`
          : '분석을 요청했습니다.',
      });
      await load({ silent: true });
    } catch (e) {
      setToast({
        type: 'error',
        text: e.httpStatus === 409 ? '본문이 확정되지 않은 문제입니다 — 본문 확정 후 분석할 수 있습니다.' : (e.message || '분석 요청에 실패했습니다.'),
      });
    } finally {
      setBusy(null);
    }
  };

  // ── 저장(PATCH) — 변경 키만 + 낙관적 잠금 ──
  const buildPatch = () => {
    const s = seededRef.current;
    const patch = {};
    if ([...conceptIds].sort().join(',') !== [...s.conceptIds].sort().join(',')) patch.concept_ids = conceptIds;
    if (difficulty !== s.difficulty) patch.difficulty = difficulty === '' ? null : Number(difficulty);
    if ((answerText ?? '') !== (s.answer ?? '')) patch.answer = answerText.trim() === '' ? null : answerText;
    return patch;
  };

  const handleSave = async ({ quiet = false } = {}) => {
    if (busy || !latest) return false;
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      if (!quiet) setToast({ type: 'info', text: '변경 사항이 없습니다.' });
      return latest; // 변경 없음 — 현재 상세가 곧 신선본
    }
    if (latest.updated_at) patch.expected_updated_at = latest.updated_at;
    setBusy('save');
    setToast(null);
    // 다시 시도하는 순간 옛 안내를 걷는다 — 또 막히면 아래 catch 가 다시 세운다.
    setStaleHint(false);
    try {
      let d = await patchAnalysis(latest.analysis_id, patch, getAccessToken);
      if (d === AUTH_REDIRECT) return false;
      // 계약이 PATCH 응답 형태를 정의하지 않는다 — 상세 전건이 아니면 재조회로 보강
      // (부분 객체를 latest로 쓰면 검수 본문이 통째로 사라진다).
      if (!d || d.analysis_id == null || !d.values) {
        d = await fetchAnalysis(latest.analysis_id, getAccessToken);
      }
      if (String(problemId) === String(problemRef.current)) {
        setSnap((prev) => ({ ...(prev || {}), latest: d }));
        seedEdits(d);
        if (!quiet) setToast({ type: 'info', text: '저장되었습니다.' });
      }
      return d; // 신선한 상세 — 승인 연쇄가 이 updated_at 을 써야 409 가 안 난다
    } catch (e) {
      setToast({ type: 'error', text: reviewErrorText(e, '저장에 실패했습니다.') });
      if (isStaleError(e)) setStaleHint(true);
      return false;
    } finally {
      setBusy(null);
    }
  };

  // ── 승인 — 고쳐 놓고 누르는 것은 한 동작이라 먼저 저장한다 ──
  const handleApprove = async () => {
    if (busy || !latest) return;
    // 덮어쓰는 칸 = 승인에 값이 실리고(slotFilled) 은행에도 값이 있는 칸. 결과가 빈 칸은 승인이 건드리지
    // 않으니 경고에서 뺀다 — 예전에는 은행 값만 보고 「덮어씁니다」라고 말해 거짓 경고가 났다(D3).
    const overwrite = ANALYSIS_TARGET_KEYS
      .filter((k) => slotFilled(k) && bankPresence?.[k])
      .map((k) => ANALYSIS_TARGET_LABEL[k]);
    if (!window.confirm(
      '이 분석 결과를 문제은행에 반영합니다.\n'
      + (overwrite.length > 0 ? `⚠ ${overwrite.join('·')}은(는) 기존 값을 덮어씁니다.\n` : '')
      + '\n승인할까요?',
    )) return;
    // 고쳐 놓고 승인은 한 동작 — 먼저 저장하고, 저장이 돌려준 '신선한 상세'의 updated_at 으로
    // 승인한다. 클로저의 구 latest.updated_at 을 쓰면 방금 저장이 잠금 키를 돌려 반드시 409 다.
    let fresh = latest;
    if (dirty) {
      const saved = await handleSave({ quiet: true });
      if (!saved) return;
      fresh = saved;
    }
    setBusy('approve');
    setToast(null);
    setStaleHint(false);
    try {
      const d = await approveAnalysis(fresh.analysis_id, { expectedUpdatedAt: fresh.updated_at }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      const appliedText = Array.isArray(d.applied) && d.applied.length > 0 ? ` (반영: ${d.applied.join(', ')})` : '';
      setToast({
        type: d.digest_mismatch ? 'error' : 'info',
        text: d.digest_mismatch
          ? `반영했지만 분석 이후 본문이 수정된 문제였습니다 — 결과를 확인하세요.${appliedText}`
          : `문제은행에 반영했습니다.${appliedText}`,
      });
      await load({ silent: true });
      onChanged && onChanged();
    } catch (e) {
      // 409 두 갈래를 `data.reason` 으로 가른다(계약 §3).
      //  - stale            : 그 사이 누가 고쳤다 → [최신 받기]로 푼다.
      //  - nothing_to_apply : 넣을 것이 없다. 실패가 아니라 "이미 다 들어가 있다"에 가깝고,
      //                       화면의 '반영됨' 표시가 실제와 어긋나 있을 수 있으니 조용히 다시 읽는다
      //                       (여기까지 왔다면 미저장 수정은 앞의 handleSave 가 이미 저장했다).
      setToast({ type: 'error', text: reviewErrorText(e, '승인에 실패했습니다.') });
      if (isStaleError(e)) setStaleHint(true);
      if (e?.data?.reason === 'nothing_to_apply') await load({ silent: true });
    } finally {
      setBusy(null);
    }
  };

  // ── 폐기 ──
  const handleDiscard = async () => {
    if (busy || !latest) return;
    if (!window.confirm('이 분석을 폐기할까요? 이력에는 남고, 다시 분석할 수 있습니다.')) return;
    setBusy('discard');
    setToast(null);
    try {
      const d = await discardAnalysis(latest.analysis_id, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setToast({ type: 'info', text: '폐기했습니다.' });
      await load({ silent: true });
      onChanged && onChanged();
    } catch (e) {
      setToast({ type: 'error', text: e.message || '폐기에 실패했습니다.' });
    } finally {
      setBusy(null);
    }
  };

  // ════ 렌더 ════
  if (problemId == null) return null;
  if (loading) return <div className="p-4 text-sm text-gray-400">분석 현황을 읽는 중…</div>;
  if (error) {
    return (
      <div className="space-y-2 p-4">
        <p className="text-sm text-danger">{error}</p>
        <Button size="xs" variant="secondary" onClick={() => load()}>다시 시도</Button>
      </div>
    );
  }

  const statusDisplay = latest ? getAnalysisStatusDisplay(latest.status) : null;
  const discarded = latest?.review_status === 400;
  // 풀이가 닿은 답과 은행 정답 비교(회신 R3) — 수정본이 정답·풀이를 고쳤으면 null(낡은 판정을 내지 않는다)
  const answerCheck = solutionAnswerCheck(latest);

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {/* 헤더 — 상태·revision·요청 버튼 */}
      <div className="flex flex-wrap items-center gap-2">
        {latest ? (
          <>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusDisplay.cls}`}>{statusDisplay.text}</span>
            <span className="text-xs text-gray-400">rev {latest.revision}</span>
            {latest.edited && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] text-sky-700">수정본 있음</span>}
            {discarded && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] text-red-700">폐기됨</span>}
          </>
        ) : (
          <span className="text-sm text-gray-500">분석 이력이 없습니다.</span>
        )}
        <span className="ml-auto" />
        {/* AI 버튼은 보라 — 검색 목록 머리의 [AI 문제 생성](ProblemSearchPanel)과 같은 값을 쓴다(오너 지시 2026-09-28).
            공용 Button 에 색을 덧씌우면 어느 bg 가 이기는지 CSS 순서에 달려 있어 그대로 옮겼다.
            잠김(opacity-50·cursor-not-allowed)과 요청 중 돌이표(size-4 border-b-2)는 종전 Button 의 것 그대로다. */}
        {canRunAi && (
          <button
            type="button"
            onClick={openAiWindow}
            disabled={busy != null || polling}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles aria-hidden="true" className="size-3.5" />
            {busy === 'request' && <span aria-hidden="true" className="size-4 animate-spin rounded-full border-b-2 border-current" />}
            {latest == null ? 'AI 분석 요청' : polling ? '분석 중…' : 'AI 분석'}
          </button>
        )}
      </div>

      {/* 승인은 문제은행에 실제로 반영된다 — 그 결과를 전하는 통로가 이 한 줄뿐이라 항상 마운트한다 */}
      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>

      {/* 처리 중 — 폴링 안내 */}
      {polling && (
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
          <RotateCw className="size-4 animate-spin text-gray-400" />
          AI가 분석하고 있습니다 — 끝나면 자동으로 갱신됩니다.
        </div>
      )}

      {/* 실패 */}
      {latest?.status === 500 && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">
          분석 실패{latest.error ? ` — ${latest.error}` : ''}. [AI 분석]으로 재시도할 수 있습니다.
        </div>
      )}

      {/* 완료라는데 정본이 비어 있다 — 배지만 남기면 사용자는 고장인지 로딩인지 알 수 없다 */}
      {latest?.status === 200 && !values && (
        <div role="status" className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-sm text-gray-600">분석은 끝났는데 결과 내용을 받지 못했습니다.</p>
          <Button size="xs" variant="secondary" onClick={() => load()}>다시 불러오기</Button>
        </div>
      )}

      {/* 검수 본문 — 완료(200)일 때 */}
      {latest?.status === 200 && values && (
        <>
          {/* 개념 */}
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">개념</span>
              <SlotBadge currentValue={current?.concept_ids} filled={slotFilled('concept_ids')} reason={slotReason('concept_ids')} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {conceptIds.map((cid) => (
                <ConceptChip
                  key={cid}
                  concept={conceptLabel(cid)}
                  onRemove={busy ? undefined : (id) => setConceptIds((prev) => prev.filter((c) => c !== id))}
                />
              ))}
              {conceptIds.length === 0 && <span className="text-xs text-gray-400">없음</span>}
            </div>
            <div className="relative mt-1.5">
              <input
                type="text"
                value={conceptQuery}
                onChange={(e) => setConceptQuery(e.target.value)}
                placeholder="개념 추가 — 이름 · 번호 · ID"
                disabled={busy != null}
                className="w-full rounded-lg border px-2 py-1 text-xs focus:border-primary focus:outline-none disabled:bg-gray-50"
              />
              {conceptQuery.trim() !== '' && (
                <div className="absolute inset-x-0 z-10 mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                  {conceptMatches.length === 0 ? (
                    <div className="p-2 text-xs text-gray-400">찾는 개념이 없습니다</div>
                  ) : conceptMatches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => { setConceptIds((prev) => (prev.includes(c.id) ? prev : [...prev, c.id])); setConceptQuery(''); }}
                      className="flex w-full items-center gap-1.5 px-2 py-1 text-left transition-colors hover:bg-primary/10"
                    >
                      <ConceptChip concept={c} />
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-gray-400">#{c.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 난이도 · 정답 — 계약상 '비우기' 연산은 없다: 값을 비워 저장하면 null 전송 = 그 수정
              철회(AI 원본 복귀)다. 저장 응답으로 화면이 즉시 원본 값으로 되돌아와 보이므로
              조용한 오해는 없지만, 문구로도 밝힌다. */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-1.5 text-sm text-gray-600">
              <span className="text-xs font-medium text-gray-500">난이도</span>
              <SlotBadge currentValue={current?.difficulty} filled={slotFilled('difficulty')} reason={slotReason('difficulty')} />
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                disabled={busy != null}
                className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
              >
                <option value="">AI 값으로 되돌리기</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{difficultyOptionLabel(n)}</option>)}
              </select>
            </label>
            {/* 정답은 **문자열 한 줄**이고 숫자가 아니다. 객관식은 문제은행 관례대로 원문자
                '①'~'⑤' 로 저장한다 — 2026-08-26 계약 §4 D2 로 AI 변형 승인분도 `'3'` 이 아니라
                `'③'` 으로 저장되면서 승격·분석 v2 와 표기가 한 벌로 맞았다. 여기 칸은 자유 입력을
                그대로 실어 보내므로(비교·변환 없음) 그 표기를 흐트러뜨리지 않는다.
                숫자 '3' 을 손으로 쳐 넣으면 옛 표기가 다시 섞이니 원문자로 적는다. */}
            {/* 정답 묶음은 최소 폭(min-w-60)을 갖는다 — 분석 칸이 좁으면(395px 실측) 난이도 옆에서 146px 로 눌려
                배지가 여러 줄로 꺾이고 입력칸이 줄었다. 모자라면 다음 줄로 내려가 한 줄을 다 쓴다(2026-09-27). */}
            <label className="flex min-w-60 flex-1 items-center gap-1.5 text-sm text-gray-600">
              <span className="shrink-0 text-xs font-medium text-gray-500">정답</span>
              <SlotBadge currentValue={current?.answer} filled={slotFilled('answer')} reason={slotReason('answer')} />
              <AnswerBox
                value={answerText}
                onChange={setAnswerText}
                placeholder="정답 없음 — 객관식은 ①②③④⑤"
                disabled={busy != null}
              />
            </label>
          </div>

          <p className="text-[10px] text-gray-400">
            난이도·정답을 비워 저장하면 값이 지워지는 것이 아니라 <b>AI 원본 값으로 되돌아갑니다</b>(수정 철회).
          </p>

          {/* 풀이 미리보기 — Block/Run (OCR 검수 렌더러 재사용) */}
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500">풀이</span>
              <SlotBadge currentValue={current?.has_solution ? true : null} filled={slotFilled('solution')} reason={slotReason('solution')} />
              {/* 새 풀이가 도달한 답 — 정답을 함께 다시 만든 결과는 서버가 비교하지 않으므로(answer_match null)
                  바로 위 정답 칸과 사람이 나란히 대 볼 수 있게 늘 적는다(회신 R4-1). 값은 정답 칸처럼 원문 그대로. */}
              {answerCheck?.solutionAnswer && (
                <span className="text-[10px] text-gray-400">풀이의 답 {answerCheck.solutionAnswer}</span>
              )}
            </div>
            {answerCheck?.mismatch && (
              <p className="mb-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                풀이가 닿은 답{answerCheck.solutionAnswer ? `(${answerCheck.solutionAnswer})` : ''}이 문제은행 정답
                {current?.answer != null ? `(${current.answer})` : ''}과 다릅니다 — 승인하기 전에 풀이를 확인하세요.
              </p>
            )}
            {Array.isArray(values.solution_blocks) && values.solution_blocks.length > 0 ? (
              <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-100 p-3">
                <Blocks blocks={values.solution_blocks} />
              </div>
            ) : (
              <p className="text-xs text-gray-400">{SOLUTION_EMPTY_TEXT[slotReason('solution')] || '생성된 풀이가 없습니다.'}</p>
            )}
          </div>

          {/* 액션 바 */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            {canApprove && (
              <button
                type="button"
                onClick={handleDiscard}
                disabled={busy != null || discarded}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 className="size-3.5" />
                {busy === 'discard' ? '폐기 중…' : '폐기'}
              </button>
            )}
            {dirty && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">수정됨 · 미저장</span>}
            <span className="ml-auto" />
            {/* 낙관락에 막힌 뒤에만 나온다(계약 §3) — 평소에는 없는 버튼이라 화면이 늘어나지 않는다 */}
            {staleHint && (
              <Button size="sm" variant="secondary" onClick={reloadFromServer} disabled={busy != null}>
                최신 받기{dirty ? '(내 수정 버림)' : ''}
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => handleSave()} loading={busy === 'save'} disabled={busy != null || !dirty}>
              저장
            </Button>
            {canApprove && (
              <Button size="sm" variant="success" onClick={handleApprove} loading={busy === 'approve'} disabled={busy != null || discarded} icon={<Check className="size-3.5" />}>
                승인 — 문제은행 반영
              </Button>
            )}
          </div>
        </>
      )}
      {/* 이 카드는 모달이 아니라 패널 안 카드다 — 창이 겹치지 않아 부모의 ESC 를 끌 필요가 없다.
          canRunAi 를 창에도 건다 — 여는 버튼만 감추면 창은 살아 있고, 나중에 다른 길로 aiOpen 을
          켜는 코드가 생기면 감췄다고 믿은 실행이 조용히 돈다. */}
      <AiRunModal
        isOpen={aiOpen && canRunAi}
        title={latest == null ? 'AI 분석 요청' : 'AI 분석'}
        summary={latest == null
          ? '이 문제를 AI 가 분석합니다. 만들 것은 아래에서 고릅니다.'
          : '다시 분석하면 새 revision 이 쌓입니다(기존 이력은 남습니다). 만들 것은 아래에서 고릅니다.'}
        warning={latest?.status === 200 && dirty
          ? '저장하지 않은 수정이 있습니다 — 새 결과가 도착하면 버려집니다.'
          : null}
        options={(
          <AnalysisTargetChecks
            targets={targets}
            onChange={setTargets}
            presence={bankPresence}
            disabled={busy === 'request'}
          />
        )}
        showOptionPriority
        confirmDisabled={!hasAnyTarget(targets)}
        placeholder="예: 풀이는 학생이 따라오게 단계별로 · 개념은 직접 쓰는 것만"
        confirmText={latest == null ? '분석 요청' : '다시 분석'}
        busy={busy === 'request'}
        onConfirm={handleRequest}
        onClose={() => setAiOpen(false)}
      />
    </div>
  );
}

export default AnalysisReviewCard;
